/**
 * Utility functions for checking document expiry dates (insurance, PUC, fitness)
 */

/**
 * Check if a document is expired or expiring soon
 * @param {string} expiryDate - Document expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {object} - { isExpired: boolean, isExpiringSoon: boolean, daysUntilExpiry: number, message: string }
 */
export const checkDocumentExpiry = (expiryDate, warningDays = 30) => {
  if (!expiryDate) {
    return {
      isExpired: false,
      isExpiringSoon: false,
      daysUntilExpiry: null,
      message: 'No expiry date set'
    };
  }

  const today = new Date();
  const expiry = new Date(expiryDate);
  
  // Reset time to start of day for accurate day calculation
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  
  const timeDiff = expiry.getTime() - today.getTime();
  const daysUntilExpiry = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  const isExpired = daysUntilExpiry < 0;
  const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= warningDays;
  
  let message = '';
  if (isExpired) {
    message = `Document expired ${Math.abs(daysUntilExpiry)} day${Math.abs(daysUntilExpiry) === 1 ? '' : 's'} ago`;
  } else if (isExpiringSoon) {
    message = `Document expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`;
  } else {
    message = `Document valid for ${daysUntilExpiry} more day${daysUntilExpiry === 1 ? '' : 's'}`;
  }
  
  return {
    isExpired,
    isExpiringSoon,
    daysUntilExpiry,
    message
  };
};

/**
 * Check if insurance is expired or expiring soon
 * @param {string} expiryDate - Insurance expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {object} - { isExpired: boolean, isExpiringSoon: boolean, daysUntilExpiry: number, message: string }
 */
export const checkInsuranceExpiry = (expiryDate, warningDays = 30) => {
  return checkDocumentExpiry(expiryDate, warningDays);
};

/**
 * Check if PUC is expired or expiring soon
 * @param {string} expiryDate - PUC expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {object} - { isExpired: boolean, isExpiringSoon: boolean, daysUntilExpiry: number, message: string }
 */
export const checkPucExpiry = (expiryDate, warningDays = 30) => {
  const result = checkDocumentExpiry(expiryDate, warningDays);
  if (result.message !== 'No expiry date set') {
    result.message = result.message.replace('Document', 'PUC');
  }
  return result;
};

/**
 * Check if fitness is expired or expiring soon
 * @param {string} expiryDate - Fitness expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {object} - { isExpired: boolean, isExpiringSoon: boolean, daysUntilExpiry: number, message: string }
 */
export const checkFitnessExpiry = (expiryDate, warningDays = 30) => {
  const result = checkDocumentExpiry(expiryDate, warningDays);
  if (result.message !== 'No expiry date set') {
    result.message = result.message.replace('Document', 'Fitness');
  }
  return result;
};

/**
 * Get alert level for document expiry
 * @param {string} expiryDate - Document expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {string} - 'error', 'warning', or 'success'
 */
export const getDocumentAlertLevel = (expiryDate, warningDays = 30) => {
  const check = checkDocumentExpiry(expiryDate, warningDays);
  
  if (check.isExpired) {
    return 'error';
  } else if (check.isExpiringSoon) {
    return 'warning';
  } else {
    return 'success';
  }
};

/**
 * Get alert level for insurance expiry
 * @param {string} expiryDate - Insurance expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {string} - 'error', 'warning', or 'success'
 */
export const getInsuranceAlertLevel = (expiryDate, warningDays = 30) => {
  return getDocumentAlertLevel(expiryDate, warningDays);
};

/**
 * Get alert level for PUC expiry
 * @param {string} expiryDate - PUC expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {string} - 'error', 'warning', or 'success'
 */
export const getPucAlertLevel = (expiryDate, warningDays = 30) => {
  return getDocumentAlertLevel(expiryDate, warningDays);
};

/**
 * Get alert level for fitness expiry
 * @param {string} expiryDate - Fitness expiry date in YYYY-MM-DD format
 * @param {number} warningDays - Number of days before expiry to show warning (default: 30)
 * @returns {string} - 'error', 'warning', or 'success'
 */
export const getFitnessAlertLevel = (expiryDate, warningDays = 30) => {
  return getDocumentAlertLevel(expiryDate, warningDays);
};

/**
 * Format date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatDocumentDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format insurance date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatInsuranceDate = (dateString) => {
  return formatDocumentDate(dateString);
};

/**
 * Format PUC date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatPucDate = (dateString) => {
  return formatDocumentDate(dateString);
};

/**
 * Format fitness date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatFitnessDate = (dateString) => {
  return formatDocumentDate(dateString);
};
